import React from 'react';
import { Image } from 'lucide-react';

interface PageHeaderConfig {
    title?: string;
    description?: string;
    logoUrl?: string;
    showLogo?: boolean;
    logoPlacement?: 'left' | 'right';
    showDataRefresh?: boolean;
    dataRefreshFormat?: string;
    style?: {
        title?: any;
        description?: any;
        plotArea?: any;
        design?: any;
        dataRefresh?: any;
    };
}

// Predefined date format map — keyed by the dropdown value stored in config
const FORMAT_MAP: Record<string, (d: Date) => string> = {
    default: (d) => new Intl.DateTimeFormat('en-US', {
        month: 'short', day: 'numeric', year: 'numeric',
        hour: 'numeric', minute: '2-digit', hour12: true,
    }).format(d),
    date_only: (d) => new Intl.DateTimeFormat('en-US', {
        month: 'long', day: 'numeric', year: 'numeric',
    }).format(d),
    date_short: (d) => new Intl.DateTimeFormat('en-US', {
        month: '2-digit', day: '2-digit', year: 'numeric',
    }).format(d),
    date_time_24: (d) => new Intl.DateTimeFormat('en-GB', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit', hour12: false,
    }).format(d),
    date_month_year: (d) => new Intl.DateTimeFormat('en-US', {
        month: 'long', year: 'numeric',
    }).format(d),
    date_iso: (d) => d.toISOString().split('T')[0],
    date_time_full: (d) => new Intl.DateTimeFormat('en-US', {
        weekday: 'short', month: 'long', day: 'numeric', year: 'numeric',
        hour: 'numeric', minute: '2-digit', hour12: true,
    }).format(d),
    time_only: (d) => new Intl.DateTimeFormat('en-US', {
        hour: 'numeric', minute: '2-digit', hour12: true,
    }).format(d),
};

// Utility to format date strings using a predefined format key
const formatDate = (dateString: string | null | undefined, formatKey: string = 'default'): string => {
    if (!dateString) return '';
    try {
        const d = new Date(dateString);
        if (isNaN(d.getTime())) return dateString; // Invalid date — return raw

        const formatter = FORMAT_MAP[formatKey] || FORMAT_MAP['default'];
        return formatter(d);
    } catch {
        return dateString;
    }
};

export const PageHeaderWidget = ({ config = {}, lastRefreshedAt }: { config?: PageHeaderConfig, lastRefreshedAt?: string }) => {
    // Defaults
    const title = config.title || "Dashboard Title";
    const description = config.description || "";
    const logoUrl = config.logoUrl || "";
    const showLogo = config.showLogo !== false; // Default true
    const logoPlacement = config.logoPlacement || 'left'; // 'left' | 'right'
    const showDataRefresh = config.showDataRefresh === true; // Default false
    const dataRefreshFormat = config.dataRefreshFormat || '';

    const titleStyle = config.style?.title || {};
    const descStyle = config.style?.description || {};
    const dataRefreshStyle = config.style?.dataRefresh || {};

    const plotStyle = config.style?.plotArea || {};
    const designStyle = config.style?.design || {};

    return (
        <div
            className="w-full h-full flex items-center overflow-hidden transition-all"
            style={{
                backgroundColor: designStyle.cardBackgroundColor || 'transparent',
                borderRadius: designStyle.borderRadius ? `${designStyle.borderRadius}px` : undefined,
                boxShadow: designStyle.shadow !== 'none' ? designStyle.shadow : undefined,
                paddingTop: plotStyle.paddingTop !== undefined ? `${plotStyle.paddingTop}px` : '0px',
                paddingBottom: plotStyle.paddingBottom !== undefined ? `${plotStyle.paddingBottom}px` : '0px',
                paddingLeft: plotStyle.paddingLeft !== undefined ? `${plotStyle.paddingLeft}px` : '16px', // Default px-4 = 16px
                paddingRight: plotStyle.paddingRight !== undefined ? `${plotStyle.paddingRight}px` : '16px',
                justifyContent: 'space-between'
            }}
        >
            {/* Left Content Group (Logo [if left] + Text) */}
            <div className={`flex items-center gap-4 flex-1 min-w-0 ${logoPlacement === 'right' ? 'flex-row' : 'flex-row'}`}>

                {/* Logo (Left) */}
                {showLogo && logoPlacement !== 'right' && (
                    <div className="shrink-0">
                        {logoUrl ? (
                            <img src={logoUrl} alt="Logo" className="h-10 w-auto object-contain rounded-sm select-none" draggable={false} />
                        ) : (
                            <div className="w-10 h-10 bg-slate-100 rounded flex items-center justify-center text-slate-400">
                                <Image className="w-5 h-5" />
                            </div>
                        )}
                    </div>
                )}

                {/* Text Block */}
                <div className="flex-1 min-w-0 flex flex-col justify-center py-2">
                    <h1
                        className="truncate"
                        style={{
                            color: titleStyle.color || '#1e293b',
                            fontSize: titleStyle.fontSize ? `${titleStyle.fontSize}px` : '24px',
                            fontWeight: titleStyle.bold ? 'bold' : 'normal',
                            fontStyle: titleStyle.italic ? 'italic' : 'normal',
                            textDecoration: titleStyle.underline ? 'underline' : 'none',
                            textAlign: titleStyle.align || 'left',
                            marginBottom: '4px',
                            lineHeight: '1.5',
                            paddingBottom: '2px'
                        }}
                    >
                        {title}
                    </h1>
                    {description && (
                        <p
                            className="truncate opacity-90"
                            style={{
                                color: descStyle.color || '#64748b',
                                fontSize: descStyle.fontSize ? `${descStyle.fontSize}px` : '14px',
                                fontWeight: descStyle.bold ? 'bold' : 'normal',
                                fontStyle: descStyle.italic ? 'italic' : 'normal',
                                textDecoration: descStyle.underline ? 'underline' : 'none',
                                textAlign: descStyle.align || 'left',
                                lineHeight: '1.5',
                                paddingBottom: '2px'
                            }}
                        >
                            {description}
                        </p>
                    )}

                    {/* Data Refresh Block (Below Description) */}
                    {showDataRefresh && (
                        <div
                            className="mt-1"
                            style={{
                                color: dataRefreshStyle.color || '#94a3b8',
                                fontSize: dataRefreshStyle.fontSize ? `${dataRefreshStyle.fontSize}px` : '12px',
                                fontWeight: dataRefreshStyle.bold ? 'bold' : 'normal',
                                fontStyle: dataRefreshStyle.italic ? 'italic' : 'normal',
                                textDecoration: dataRefreshStyle.underline ? 'underline' : 'none',
                                textAlign: dataRefreshStyle.align || 'left',
                            }}
                        >
                            <span 
                                style={{
                                    backgroundColor: dataRefreshStyle.backgroundColor || 'transparent',
                                    padding: dataRefreshStyle.backgroundColor ? '2px 6px' : '0',
                                    borderRadius: dataRefreshStyle.backgroundColor ? '4px' : '0',
                                }}
                            >
                                Refreshed: {lastRefreshedAt ? formatDate(lastRefreshedAt, dataRefreshFormat) : 'N/A (Preview)'}
                            </span>
                        </div>
                    )}
                </div>
            </div>

            {/* Logo (Right) */}
            {showLogo && logoPlacement === 'right' && (
                <div className="shrink-0 ml-4">
                    {logoUrl ? (
                        <img src={logoUrl} alt="Logo" className="h-10 w-auto object-contain rounded-sm select-none" draggable={false} />
                    ) : (
                        <div className="w-10 h-10 bg-slate-100 rounded flex items-center justify-center text-slate-400">
                            <Image className="w-5 h-5" />
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};
